import { IForm } from 'core-components/classes/models/IForm'
import { NotificationController } from 'core-components/library/popup'
import { IUser } from 'core-components/sitebuilder/models/IUser'
import {
  IDataProviderFilter,
  IDataProviderSort,
  IFilter,
  IPaging,
  IPagingFromTo,
  Logic,
  Operator,
} from 'core-components/types'
import StorageKeys from 'src/core/classes/cache/StorageKeys'
import { getAppId, getAppOwnerId } from 'src/core/classes/helpers/getAppId'
import { isCoreServiceAvailable } from 'src/core/classes/helpers/isCoreServiceAvailable'
import { setTokens } from 'src/core/classes/helpers/StorageHelper'
import { proxyIdentity } from 'src/core/classes/helpers/UrlHelper'
import { Rest } from 'src/core/classes/http'
import { IUserApp } from 'src/core/classes/models/IUserApp'
import { IUserInfo } from 'src/core/classes/models/IUserInfo'
import { IApiUser, IJsonReference, IShortUser, map as mapUser } from 'src/core/classes/models/User'
import VersionStoreService from 'src/core/classes/services/VersionStoreService'
import { userEventBus } from 'src/core/hooks/services/useUserStore'
import Logger from 'src/core/utils/Logger'

class UserApi {
  public fetchUsers = async (
    filter: IDataProviderFilter,
    paging: IPaging | IPagingFromTo,
    sort?: IDataProviderSort | IDataProviderSort[]
  ): Promise<{ Data: IUser[]; Total: number }> => {
    const url = proxyIdentity('/mbe/api/v3/app/users/search')
    const newPaging = paging as IPaging

    const { filters, roles } = this.getFiltersAndRolesForUserSearch(filter)

    const payload: IForm = {
      Filter: filters,
      PageSize: newPaging.pageSize,
      Page: newPaging.page + 1,
      Roles: roles,
    }

    if (sort) {
      payload.Sort = sort
    }

    try {
      const response = await Rest.post({ url, payload, identityToken: true })
      const rows: IApiUser[] = (response && response['Data']) || []

      const users = rows.map((row) => mapUser(row))

      return {
        Data: users,
        Total: response.Total || users.length,
      }
    } catch (err) {
      NotificationController.showError({ message: (err as Error).message })
      return {
        Data: [],
        Total: 0,
      }
    }
  }

  // Fetch users by emails or UIDs
  public fetchUsersByIds = async (ids: string[]): Promise<IShortUser[]> => {
    if (!ids.length) {
      return []
    }
    const url = proxyIdentity('/mbe/api/v3/app/users/lookup')
    const payload: IForm = {
      Ids: ids,
    }
    try {
      const response = await Rest.post({ url, payload, identityToken: true })
      return response['Data'] || []
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Something went wrong while fetching users list'
      NotificationController.showError({ message: errorMessage })
      return []
    }
  }

  public fetchAppById = async (id: string): Promise<IUser | null> => {
    if (!id) {
      return null
    }
    const url = proxyIdentity('/mbe/api/v3/app/users/search')
    const payload: IForm = {
      PageSize: 1,
      Page: 1,
    }

    if (isCoreServiceAvailable()) {
      payload.Filter = {
        logic: Logic.AND,
        filters: [{ field: 'id', value: id, operator: Operator.EQUAL }],
      }
    } else {
      payload.Filter = { field: 'Id', value: id, operator: Operator.EQUAL }
      payload.Sort = {}
    }

    try {
      const response = await Rest.post({ url, payload, identityToken: true })
      const rows: IApiUser[] = response['Data'] || []
      if (rows.length) {
        return rows.map((row) => mapUser(row))[0]
      }
      return null
    } catch (err) {
      NotificationController.showError({ message: (err as Error).message })
      return null
    }
  }

  public fetchAnyById = async ({ uid }: { uid: string }): Promise<IUser | null> => {
    try {
      const url = proxyIdentity(`/mbe/api/v3/users/${uid}`)
      const row: IApiUser = await Rest.get({ url, identityToken: true })
      const users = [mapUser(row)]
      return users.length ? users[0] : null
    } catch (err: unknown) {
      NotificationController.showError({ message: (err as Error).message })
      return null
    }
  }

  public fetchAppsById = async (id: string): Promise<IUserApp[]> => {
    try {
      const user = await this.fetchAppById(id)
      const url = proxyIdentity(`/mbe/api/v3/users/${user?.UID}/apps`)
      const data = await Rest.get({ url, identityToken: true })
      return data || []
    } catch (err) {
      NotificationController.showError({ message: (err as Error).message })
      return []
    }
  }

  public updateUserApp = async (
    id: string,
    appId: string,
    userInfo: IUserInfo
  ): Promise<IUserApp[] | null> => {
    try {
      const user = await this.fetchAppById(id)
      const url = proxyIdentity(`/mbe/api/v3/users/${user?.UID}/apps/${appId}`)
      return await Rest.put({ url, payload: userInfo, identityToken: true })
    } catch (err) {
      NotificationController.showError({ message: (err as Error).message })
      return []
    }
  }

  public deleteUserApp = async (id: string, appId: string) => {
    try {
      const user = await this.fetchAppById(id)
      const url = proxyIdentity(`/mbe/api/v3/users/${user?.UID}/apps/${appId}`)
      return await Rest.delete({ url, identityToken: true })
    } catch (err) {
      NotificationController.showError({ message: (err as Error).message })
      return null
    }
  }

  public fetchByName = async (name: string): Promise<IUser[]> => {
    let filter: IDataProviderFilter = {
      logic: Logic.OR,
      filters: [
        {
          field: 'firstName',
          operator: Operator.CONTAINS,
          value: name,
        },
        {
          field: 'lastName',
          operator: Operator.CONTAINS,
          value: name,
        },
        {
          field: 'Id',
          operator: Operator.CONTAINS,
          value: name,
        },
      ],
    }

    const personNameParts = name.split(' ')
    if (personNameParts.length >= 2) {
      filter = {
        logic: Logic.OR,
        filters: [
          {
            field: 'firstName',
            operator: Operator.CONTAINS,
            value: name,
          },
          {
            field: 'lastName',
            operator: Operator.CONTAINS,
            value: name,
          },
          {
            logic: Logic.AND,
            filters: [
              {
                field: 'firstName',
                operator: Operator.STARTSWITH,
                value: name.split(' ')[0],
              },
              {
                field: 'lastName',
                operator: Operator.STARTSWITH,
                value: name.split(' ')[1],
              },
            ],
          },
        ],
      }
    }
    const { Data: rows } = await this.fetchUsers(filter, { page: 0, pageSize: 5 })
    return rows
  }

  // TODO: Remove (use in LUT)
  /**
   * Detach user from application
   * @param id
   */
  public detachFromApp = async (id: string) => {
    try {
      const user = await this.fetchAppById(id)
      const url = proxyIdentity(`/mbe/api/v3/app/users/${user?.UID}`)
      await Rest.delete({ url, identityToken: true })
      userEventBus.notify('detach', id)
    } catch (err) {
      NotificationController.showError({ message: (err as Error).message })
    }
  }

  // TODO: Remove (use in LUT)
  public signInGuest = async (name: string): Promise<boolean> => {
    const url = proxyIdentity('/mbe/api/v2/access/token/for/guest')
    const payload = {
      AppId: getAppId(),
      FirstName: name,
      LastName: 'User',
      Language: 'en',
      CountryCode: 'US',
    }
    try {
      const result = await Rest.post({ url, payload, identityToken: true })
      if (result) {
        setTokens(getAppOwnerId(), result['AccessToken'], result['RefreshToken'])
        return true
      }
      return false
    } catch (err) {
      NotificationController.showError({ message: (err as Error).message })
      return false
    }
  }

  public saveUser = async (user: IUser, appId?: string) => {
    // While we work with 495 form we can't get UID from MBE for user
    if (user.id && !user.UID) {
      user.UID = (await userApi.fetchAppById(user.id))?.UID || ''
    }

    const payload = this.getPayloadForUser(user, appId)

    let id: string
    let UID: string = ''
    if (user.id) {
      id = await this.updateUser(user, payload)
    } else {
      const createdUserData = await this.createUser(user, payload)
      id = createdUserData.id
      UID = createdUserData.UID
    }

    if (id) {
      userEventBus.notify('save', { ...user, id, UID })
    } else {
      NotificationController.showError({
        message: `The user can't be saved. Try again later or contact your administrator`,
      })
    }

    return { id, UID }
  }

  private updateUser = async (user: IUser, payload: Partial<IApiUser>): Promise<string> => {
    try {
      const url = proxyIdentity(`/mbe/api/v3/app/users/${user.UID}`)
      await Rest.put({ url, payload: { ...payload, Roles: user.roleIds }, identityToken: true })
      await VersionStoreService.updateApiVersion(StorageKeys.user)
      return user.id
    } catch (err: unknown) {
      NotificationController.showError({ message: (err as Error).message })
      return ''
    }
  }

  private convertUserFilters = (filter: IDataProviderFilter | IFilter) => {
    if (filter['logic'] === undefined) {
      // instance of IFilter
      if (
        (filter['field'] && filter['field'] === 'email') ||
        (filter['field'] && filter['field'] === 'Id')
      ) {
        return {
          ...filter,
          field: filter['field'] === 'email' ? 'Id' : filter['field'],
        }
      } else {
        return filter
      }
    } else {
      return { ...filter, filters: filter['filters'].map(this.convertUserFilters) }
    }
  }

  private getRolesFromFilters = (filter: IDataProviderFilter | IFilter) => {
    return this.getValuesFromFilters(filter, 'Roles')
  }

  private getValuesFromFilters = (
    filter: IDataProviderFilter | IFilter,
    fieldName: string
  ): string[] => {
    if (filter['logic'] === undefined) {
      if (filter['field'] === fieldName) {
        return Array.isArray(filter['value']) ? filter['value'] : [filter['value']]
      } else {
        return []
      }
    } else {
      return filter['filters']
        .map((el: IDataProviderFilter) => this.getValuesFromFilters(el, fieldName))
        .flat()
    }
  }

  private getFilterWithoutRoles = (
    filter: IDataProviderFilter | IFilter
  ): IDataProviderFilter | IFilter => {
    if (Array.isArray(filter['filters'])) {
      const logic = filter['logic']
      const filters = filter['filters'].map(this.getFilterWithoutRoles).filter((val) => {
        if (typeof val['field'] === 'string') {
          return val['field'] !== 'Roles'
        }
        return true
      })
      return { logic, filters }
    } else {
      return filter
    }
  }

  private getFiltersAndRolesForUserSearch = (filter: IDataProviderFilter) => {
    const roles = this.getRolesFromFilters(filter)
    const filterWithotRoles = this.getFilterWithoutRoles(filter)
    const filters = this.convertUserFilters(filterWithotRoles)

    return { roles, filters }
  }

  private createUser = async (
    user: IUser,
    payload: Partial<IApiUser>
  ): Promise<{ id: string; UID: string }> => {
    const userId = user.email
    const url = proxyIdentity(`/mbe/api/v3/app/users/${user.UID}`)

    payload.Id = userId
    payload.Type = 'Web'

    let newUser: IUser | null = null

    try {
      const userData = await Rest.put({
        url,
        payload: { ...payload, Roles: user.roleIds },
        identityToken: true,
      })
      if (userData) {
        const newUsers = [userData].map((row) => mapUser(row))
        if (newUsers.length) {
          newUser = newUsers[0]
        }
      } else {
        Logger.warn('User already exists in another application')
      }
    } catch (e) {
      Logger.error(e)
    }

    return { id: newUser?.id || '', UID: newUser?.UID || '' }
  }

  private getPayloadForUser = (user: IUser, appId?: string) => {
    const userCountries: IJsonReference = { value: '', list: [] }
    if (user.countries.length > 0) {
      userCountries.value = user.country
      userCountries.list = user.countries
    }

    const hpOrgs: IJsonReference = { value: '', list: [] }
    if (user.hpOrgs.length > 0) {
      hpOrgs.value = user.hpOrg
      hpOrgs.list = user.hpOrgs
    }

    const geoMarkets: IJsonReference = { value: '', list: [] }
    if (user.geoMarkets.length > 0) {
      geoMarkets.value = user.geoMarket
      geoMarkets.list = user.geoMarkets
    }

    const userLanguages: IJsonReference = { value: '', list: [] }
    if (user.languages.length > 0) {
      userLanguages.value = user.language
      userLanguages.list = user.languages
    }

    const userLocales: IJsonReference = { value: '', list: [] }
    if (user.locales.length > 0) {
      userLocales.value = user.locale
      userLocales.list = user.locales
    }

    const payload: Partial<IApiUser> = {
      FirstName: user.firstName,
      LastName: user.lastName,
      Country: JSON.stringify(userCountries),
      Locale: JSON.stringify(userLocales),
      HpOrg: JSON.stringify(hpOrgs),
      GeoMarket: JSON.stringify(geoMarkets),
      Language: JSON.stringify(userLanguages),
    }

    if (appId) {
      payload.AppId = appId
    }

    if (!!user.password) {
      payload.Password = user.password
    }

    return payload
  }
}

export const userApi = new UserApi()
